/**
 * Funnel Templates por Industria
 *
 * Templates pre-configurados listos para funcionar: cada etapa lleva
 * `objetivo` + `ask` (los dos campos que el editor escribe y el runtime
 * inyecta al system prompt como guión) y el framework de ventas recomendado.
 *
 * Reglas de contenido (reescritura 2026-08-04):
 * - SIN placeholders en los mensajes: los datos duros (precios, plazos,
 *   montos, garantías) los toma el bot de Conocimiento/Repositorio en
 *   runtime. Donde el dato es imprescindible se indica en el OBJETIVO.
 * - SIN el nombre del negocio hardcodeado (ya está en Conocimiento).
 * - Consultivos (spin/empathy/persuasion): 5 etapas. Transaccionales
 *   (straight_line): 4 etapas.
 * - `strict: false` es requerido por validatePlaybookSchema (sin él,
 *   activateBot rechaza el playbook).
 */

import i18n from '../i18n';

function translateTemplate(t) {
  if (!t) return t;
  return {
    ...t,
    name: i18n.t(`bots:funnelTemplates.${t.id}.name`, { defaultValue: t.name }),
    description: i18n.t(`bots:funnelTemplates.${t.id}.description`, { defaultValue: t.description }),
  };
}

export const FUNNEL_TEMPLATES = {
  salud_estetica: {
    id: 'salud_estetica',
    name: 'Salud y Estética',
    icon: '💊💅',
    description: 'Funnel de acogida y agendamiento para clínicas dentales, médicas y estéticas',
    industry: 'salud_estetica',
    recommendedSalesFramework: 'empathy',
    playbook: {
      name: 'Acogida y Evaluación',
      description: 'Acompañar con calidez a quien consulta por un tratamiento hasta agendar su evaluación, sin presionar.',
      start_state: 'Acogida',
      strict: false,
      states: {
        'Acogida': {
          objetivo: 'Recibir con calidez y entender qué le preocupa. No apurar ni vender — primero escuchar.',
          ask: '¡Hola! Cuéntame qué te trae por acá, con confianza.',
          action: null,
          terminal: false,
        },
        'Escucha y validación': {
          objetivo: 'Reflejar lo que dijo antes de responder. Entender hace cuánto le pasa y si ya consultó antes. Validar la molestia o inseguridad sin minimizarla.',
          ask: 'Entiendo, eso incomoda bastante. ¿Desde cuándo lo vienes notando?',
          action: null,
          terminal: false,
        },
        'Orientación': {
          objetivo: 'Explicar en lenguaje simple qué tratamientos existen para lo que describe. Nunca diagnosticar ni prometer resultados — eso lo define el profesional.',
          ask: 'Hay varias alternativas para eso. Te cuento en general cómo funcionan y el profesional lo define en la evaluación.',
          action: null,
          terminal: false,
        },
        'Resolver dudas': {
          objetivo: 'Responder inquietudes sobre duración, molestias, recuperación o valores usando Conocimiento. Ser paciente: si duda, no presionar.',
          ask: '¿Hay algo que te genere dudas? Prefiero que vayas tranquila y con toda la información.',
          action: null,
          terminal: false,
        },
        'Cierre — hora': {
          objetivo: 'Agendar evaluación. Pedir nombre, correo y disponibilidad. Si no está lista, ofrecer quedar disponible sin insistir. Ofrecer 3 o 4 horarios concretos, no la lista completa. Aclarar que si ninguno le acomoda puede indicar el día y hora que prefiera.',
          ask: '¿Te gustaría agendar una evaluación? Dime qué días te acomodan.',
          action: null,
          terminal: true,
        },
      },
      stateOrder: ['Acogida', 'Escucha y validación', 'Orientación', 'Resolver dudas', 'Cierre — hora'],
      tools_allowed: ['getCalendarSlots', 'createBooking', 'sendEmail'],
    },
  },

  inmobiliaria: {
    id: 'inmobiliaria',
    name: 'Bienes Raíces',
    icon: '🏠',
    description: 'Funnel consultivo SPIN para calificar y agendar visitas de propiedades',
    industry: 'inmobiliaria',
    recommendedSalesFramework: 'spin',
    playbook: {
      name: 'Búsqueda de Propiedades',
      description: 'Calificar con método SPIN a quien busca propiedad y llevarlo a una visita agendada.',
      start_state: 'Apertura',
      strict: false,
      states: {
        'Apertura': {
          objetivo: 'Saludar y entender si busca comprar, vender o arrendar, y en qué comuna o zona.',
          ask: '¡Hola! ¿Estás buscando comprar, vender o arrendar? Cuéntame en qué zona te interesa.',
          action: null,
          terminal: false,
        },
        'Situación': {
          objetivo: 'Conocer el contexto: tipo de propiedad, presupuesto aproximado, plazos, si es primera vivienda o inversión.',
          ask: '¿Qué tipo de propiedad tienes en mente y en qué plazo te gustaría concretar?',
          action: null,
          terminal: false,
        },
        'Problema e implicación': {
          objetivo: 'Detectar qué le ha costado hasta ahora — propiedades que no calzan, visitas perdidas, financiamiento. Amplificar el costo de seguir buscando solo.',
          ask: '¿Has visto propiedades que no terminaron de convencerte? ¿Qué fue lo que faltó?',
          action: null,
          terminal: false,
        },
        'Propuesta': {
          objetivo: 'Presentar opciones concretas del portafolio que calcen con lo conversado. Consultar el repositorio antes de sugerir.',
          ask: 'Con lo que me cuentas, tengo algunas opciones que podrían calzar. Te las comparto.',
          action: null,
          terminal: false,
        },
        'Cierre — visita': {
          objetivo: 'Agendar visita a terreno o videollamada. Pedir nombre, correo y disponibilidad horaria. Ofrecer 3 o 4 horarios concretos, no la lista completa. Aclarar que si ninguno le acomoda puede indicar el día y hora que prefiera.',
          ask: '¿Te gustaría coordinar una visita para verla en persona? Dime qué días te acomodan.',
          action: null,
          terminal: true,
        },
      },
      stateOrder: ['Apertura', 'Situación', 'Problema e implicación', 'Propuesta', 'Cierre — visita'],
      tools_allowed: ['searchCatalog', 'getProduct', 'getCalendarSlots', 'createBooking'],
    },
  },

  ecommerce: {
    id: 'ecommerce',
    name: 'E-commerce',
    icon: '🛒',
    description: 'Funnel directo Straight Line para llevar del interés a la compra',
    industry: 'ecommerce',
    recommendedSalesFramework: 'straight_line',
    playbook: {
      name: 'Venta Online',
      description: 'Llevar al cliente del producto que busca al link de pago, sin rodeos.',
      start_state: 'Bienvenida',
      strict: false,
      states: {
        'Bienvenida': {
          objetivo: 'Saludar breve y preguntar qué producto busca. Ir al grano.',
          ask: '¡Hola! ¿Qué producto estás buscando?',
          action: null,
          terminal: false,
        },
        'Búsqueda': {
          objetivo: 'Encontrar el producto en el catálogo y confirmar disponibilidad, talla, color o variante. Consultar el catálogo antes de afirmar stock.',
          ask: 'Perfecto, déjame revisar disponibilidad. ¿Alguna preferencia de talla o color?',
          action: null,
          terminal: false,
        },
        'Detalles y objeciones': {
          objetivo: 'Dar precio, plazos de despacho y formas de pago desde Conocimiento. Responder objeciones con confianza y sin rodeos.',
          ask: 'Te paso el detalle: precio, despacho y formas de pago.',
          action: null,
          terminal: false,
        },
        'Cierre': {
          objetivo: 'Llevar a la compra. Enviar link de pago o confirmar el pedido con dirección y datos.',
          ask: '¿Lo dejamos listo? Te envío el link para completar la compra.',
          action: null,
          terminal: true,
        },
      },
      stateOrder: ['Bienvenida', 'Búsqueda', 'Detalles y objeciones', 'Cierre'],
      tools_allowed: ['searchCatalog', 'getProduct', 'createCheckoutLink', 'sendEmail'],
    },
  },

  restaurante: {
    id: 'restaurante',
    name: 'Restaurante',
    icon: '🍽️',
    description: 'Funnel directo para reservas de mesa y pedidos',
    industry: 'restaurante',
    recommendedSalesFramework: 'straight_line',
    playbook: {
      name: 'Reservas y Pedidos',
      description: 'Resolver rápido una reserva de mesa o un pedido, confirmando el detalle antes de cerrar.',
      start_state: 'Bienvenida',
      strict: false,
      states: {
        'Bienvenida': {
          objetivo: 'Saludar breve y distinguir rápido: reserva de mesa, pedido para delivery o retiro.',
          ask: '¡Hola! ¿Quieres reservar mesa o hacer un pedido?',
          action: null,
          terminal: false,
        },
        'Detalle': {
          objetivo: 'Si es reserva: fecha, hora y cantidad de personas. Si es pedido: qué platos, cantidades y comentarios especiales. Consultar el menú en Conocimiento.',
          ask: 'Perfecto. ¿Para cuántas personas y a qué hora?',
          action: null,
          terminal: false,
        },
        'Confirmación': {
          objetivo: 'Repetir el detalle completo para confirmar. Informar total, tiempo de espera y formas de pago.',
          ask: 'Te confirmo el detalle antes de dejarlo listo.',
          action: null,
          terminal: false,
        },
        'Cierre': {
          objetivo: 'Cerrar reserva o pedido. Pedir nombre y teléfono, y dirección si es delivery.',
          ask: 'Listo, solo necesito tu nombre y teléfono para dejarlo confirmado.',
          action: null,
          terminal: true,
        },
      },
      stateOrder: ['Bienvenida', 'Detalle', 'Confirmación', 'Cierre'],
      tools_allowed: ['searchCatalog', 'getCalendarSlots', 'createBooking', 'sendEmail'],
    },
  },

  abogado: {
    id: 'abogado',
    name: 'Servicios Legales',
    icon: '⚖️',
    description: 'Funnel consultivo SPIN para calificar casos y agendar consultas',
    industry: 'legal',
    recommendedSalesFramework: 'spin',
    playbook: {
      name: 'Consultoría Legal',
      description: 'Calificar el caso con seriedad y llevarlo a una consulta inicial agendada. El bot no da asesoría legal.',
      start_state: 'Apertura',
      strict: false,
      states: {
        'Apertura': {
          objetivo: 'Saludar con seriedad y entender el área del caso (laboral, familia, civil, comercial). No dar asesoría legal.',
          ask: 'Hola, gracias por escribir. Cuéntame brevemente en qué tipo de situación necesitas ayuda.',
          action: null,
          terminal: false,
        },
        'Situación': {
          objetivo: 'Conocer los hechos básicos: cuándo ocurrió, si hay plazos legales corriendo, si ya hubo gestiones previas o abogado.',
          ask: '¿Desde cuándo se arrastra esto y has hecho alguna gestión hasta ahora?',
          action: null,
          terminal: false,
        },
        'Problema e implicación': {
          objetivo: 'Entender qué está en juego y qué pasa si no actúa. Detectar urgencia por plazos.',
          ask: '¿Qué es lo que más te preocupa de cómo puede terminar esto?',
          action: null,
          terminal: false,
        },
        'Propuesta': {
          objetivo: 'Explicar cómo se aborda un caso así y qué implica una asesoría formal. Nunca prometer resultados ni dar un diagnóstico legal.',
          ask: 'Es un caso que conviene revisar con detalle. Te explico cómo trabajamos y qué incluye la primera consulta.',
          action: null,
          terminal: false,
        },
        'Cierre — consulta': {
          objetivo: 'Agendar consulta inicial. Pedir nombre completo, correo y disponibilidad. Ofrecer 3 o 4 horarios concretos, no la lista completa. Aclarar que si ninguno le acomoda puede indicar el día y hora que prefiera.',
          ask: '¿Te gustaría agendar una consulta para revisarlo? Dime qué días y horarios te acomodan.',
          action: null,
          terminal: true,
        },
      },
      stateOrder: ['Apertura', 'Situación', 'Problema e implicación', 'Propuesta', 'Cierre — consulta'],
      tools_allowed: ['getCalendarSlots', 'createBooking', 'sendEmail'],
    },
  },

  taller_mecanico: {
    id: 'taller_mecanico',
    name: 'Taller Mecánico',
    icon: '🔧',
    description: 'Funnel consultivo SPIN para diagnosticar y agendar revisiones',
    industry: 'automotriz',
    recommendedSalesFramework: 'spin',
    playbook: {
      name: 'Servicios Automotrices',
      description: 'Entender el problema del vehículo y llevarlo a una revisión agendada, sin diagnosticar a ciegas.',
      start_state: 'Apertura',
      strict: false,
      states: {
        'Apertura': {
          objetivo: 'Saludar y saber qué vehículo tiene (marca, modelo, año) y qué le está pasando.',
          ask: '¡Hola! ¿Qué auto tienes y qué problema estás notando?',
          action: null,
          terminal: false,
        },
        'Situación': {
          objetivo: 'Recabar síntomas: cuándo aparece, hace cuánto, si hace ruido, si se enciende alguna luz, último mantenimiento.',
          ask: '¿Desde cuándo lo notas y en qué momento aparece — al partir, en marcha, al frenar?',
          action: null,
          terminal: false,
        },
        'Problema e implicación': {
          objetivo: 'Explicar en simple qué podría estar pasando y el riesgo de seguir manejando así. No dar diagnóstico definitivo sin revisar.',
          ask: 'Por lo que describes conviene revisarlo pronto: si se deja pasar suele salir más caro.',
          action: null,
          terminal: false,
        },
        'Propuesta': {
          objetivo: 'Explicar cómo funciona la revisión y qué incluye. Consultar precios y plazos en Conocimiento si los piden.',
          ask: 'Lo primero es un diagnóstico para confirmar. Te cuento cómo funciona y qué demora.',
          action: null,
          terminal: false,
        },
        'Cierre — agendar': {
          objetivo: 'Agendar la revisión. Pedir nombre, patente y disponibilidad. Ofrecer 3 o 4 horarios concretos, no la lista completa. Aclarar que si ninguno le acomoda puede indicar el día y hora que prefiera.',
          ask: '¿Cuándo te acomoda traerlo? Tengo horas disponibles esta semana.',
          action: null,
          terminal: true,
        },
      },
      stateOrder: ['Apertura', 'Situación', 'Problema e implicación', 'Propuesta', 'Cierre — agendar'],
      tools_allowed: ['getCalendarSlots', 'createBooking', 'sendEmail'],
    },
  },

  otros_servicios: {
    id: 'otros_servicios',
    name: 'Otros Servicios',
    icon: '💼',
    description: 'Funnel general para servicios profesionales de cualquier rubro',
    industry: 'otros',
    recommendedSalesFramework: 'persuasion',
    playbook: {
      name: 'Servicios Profesionales',
      description: 'Generar confianza con casos reales del negocio y llevar la conversación a una reunión o contratación.',
      start_state: 'Apertura',
      strict: false,
      states: {
        'Apertura': {
          objetivo: 'Saludar y entender qué servicio necesita y para qué lo requiere.',
          ask: '¡Hola! Cuéntame qué necesitas y con gusto te oriento.',
          action: null,
          terminal: false,
        },
        'Situación': {
          objetivo: 'Conocer el contexto: para cuándo lo necesita, si ha contratado algo similar antes, qué resultado espera.',
          ask: '¿Para cuándo lo necesitas y has trabajado con alguien en esto antes?',
          action: null,
          terminal: false,
        },
        'Confianza y prueba social': {
          objetivo: 'Demostrar experiencia con casos similares reales del negocio. Consultar Conocimiento y Repositorio antes de afirmar.',
          ask: 'Hemos trabajado con varios casos parecidos al tuyo. Te cuento cómo lo resolvimos.',
          action: null,
          terminal: false,
        },
        'Propuesta de valor': {
          objetivo: 'Explicar qué incluye el servicio y qué lo diferencia. Si hay disponibilidad limitada o condiciones vigentes reales, mencionarlas — nunca inventar urgencia.',
          ask: 'Te explico qué incluye el servicio y cómo lo adaptamos a lo que necesitas.',
          action: null,
          terminal: false,
        },
        'Cierre': {
          objetivo: 'Agendar una reunión o cerrar la contratación. Pedir nombre, correo y disponibilidad. Ofrecer 3 o 4 horarios concretos, no la lista completa. Aclarar que si ninguno le acomoda puede indicar el día y hora que prefiera.',
          ask: '¿Te gustaría que coordinemos una reunión para afinar los detalles?',
          action: null,
          terminal: true,
        },
      },
      stateOrder: ['Apertura', 'Situación', 'Confianza y prueba social', 'Propuesta de valor', 'Cierre'],
      tools_allowed: ['getCalendarSlots', 'createBooking', 'sendEmail'],
    },
  },
};

// Helper para obtener template por ID
export function getFunnelTemplate(templateId) {
  const t = FUNNEL_TEMPLATES[templateId] || null;
  return translateTemplate(t);
}

// Listar todas las industrias disponibles
export function getAllIndustries() {
  return Object.values(FUNNEL_TEMPLATES).map(t => translateTemplate({
    id: t.id,
    name: t.name,
    icon: t.icon,
    description: t.description,
    industry: t.industry,
    recommendedSalesFramework: t.recommendedSalesFramework
  }));
}

// Obtener framework recomendado por template
export function getRecommendedFrameworkForTemplate(templateId) {
  const template = FUNNEL_TEMPLATES[templateId];
  return template?.recommendedSalesFramework || 'spin'; // Default a SPIN
}
